import React, { useState } from 'react';
import { getTour, TourEdit, getTourEvents, getTourSalesStats } from '../../../model/tour';
import { useRemoteData, usePaginatedResponse, useData } from '../../../model/data';
import { useRouterParam, useQueryAsState } from '../../../model/history';
import { useMeta, getPageTitle } from '../../../model/meta';
import { dig, formatDateTime } from '../../../util/util';
import { toursPath } from '../../../routes';
import { Container } from '../../../components/Container/Container';
import { Card } from '../../../components/Card/Card';
import { LoadingPage } from '../../../components/LoadingPage/LoadingPage';
import { KeyValueTable } from '../../../components/KeyValueTable/KeyValueTable';
import { TourForm } from './TourCard/TourForm';
import { PermissionButton } from '../../../components/Button/PermissionButton';
import { Grid } from '../../../components/Grid/Grid';
import { Category } from '../../../model/category';
import { ControlledModal } from '../../../components/Modal/ControlledModal';
import { t } from '../../../locales/translate';
import { EventsList } from '../Events/EventsList';
import { TourTicketDist } from './TourTicketDist';

import './TourDetails.css';
import { TourGeneralInfo } from './TourGeneralInfo';
import { compact } from 'lodash';
import { Pagination } from '../../../components/Pagination/Pagination';
import { Stack } from '../../../components/Stack/Stack';

import { SalesOverTime } from '../Events/Event/Overview/sales_over_time/sales_over_time';
import { SalesByGender } from '../Events/Event/Overview/sales_by_gender/sales_by_gender';
import { SalesHeatmap } from '../Events/Event/Overview/SalesHeatmap/SalesHeatmap';
import { Meta } from '../../../components/meta/meta';

export function TourDetails(): JSX.Element {
  const id = useRouterParam<{ id: string }>().id;

  const [tour, loading, error] = useRemoteData(
    async () => {
      await getTour(id);
      getTourSalesStats(id);
    },
    (db) => db.table('tours').where({ id })[0],
    [id]
  );

  const [displayModal, setDisplayModal] = useState(false);
  const [params] = useQueryAsState();

  useMeta({
    title: getPageTitle(loading, dig(tour, 'name'), tour),
    breadcrumbs: [
      { title: t.model.tour._name, url: toursPath() },
      {
        title: getPageTitle(loading, dig(tour, 'name'), tour),
      },
    ],
  });

  const eventsPaginator = usePaginatedResponse(
    (page, perPage) =>
      getTourEvents({
        tourId: id,
        page,
        perPage,
        sortBy: params.sortBy || 'happening_from',
        sortDir: params.sortDir || 'asc',
      }),
    JSON.stringify(params),
    'events'
  );

  const events = useData((db) => compact(eventsPaginator.currentIds.map((e) => db.table('events').find(e))));

  if (loading) {
    return <LoadingPage title={t.actions.loading(t.model.tour._name)} />;
  } else if (error || !tour) {
    return <Container>{t.errors.itemNotFound(t.model.tour._name)}</Container>;
  }

  return (
    <Container className="tour-details">
      <Stack size="lg">
        <TourGeneralInfo tour={tour} />
        <Grid>
          <Card
            title={t.general.generalInformation}
            elevation={1}
            className="tour-details__details"
            actions={
              <PermissionButton name="edit" model={tour} permission="update" onClick={() => setDisplayModal(true)} />
            }
          >
            <KeyValueTable
              items={[
                {
                  key: t.model.tour.internalTitle,
                  value: tour.internalName,
                },
                {
                  key: t.model.datetime.startDate,
                  value: formatDateTime(tour.startDate),
                },
                {
                  key: t.model.datetime.endDate,
                  value: formatDateTime(tour.endDate),
                },
                {
                  key: t.model.artist._plural,
                  value: tour?.artists && (tour.artists as Category[]).map((a) => a.name).join(', '),
                },
                {
                  key: t.model.product.excludeFromFeeds,
                  value: tour.excludeFromFeeds ? t.general.yes : t.general.no,
                },
                {
                  key: 'title',
                  value: t.model.shop._name,
                },
                {
                  key: t.model.datetime.publishDate,
                  value: formatDateTime(tour?.publishedFrom),
                },
                {
                  key: t.model.datetime.unPublishDate,
                  value: formatDateTime(tour?.publishedUntil),
                },
                {
                  key: 'title',
                  value: t.model.tour.sortOrder,
                },
                {
                  key: t.model.tour.sortOrder,
                  value: tour.order,
                },
                {
                  key: t.model.tour.sortOrderHomepage,
                  value: tour.sortOrderHomepage,
                },
                {
                  key: t.model.tour.sortOrderTicketTopRight,
                  value: tour.sortOrderTicketTopRight,
                },
                {
                  key: t.model.tour.sortOrderTicketTopLeft,
                  value: tour.sortOrderTicketTopLeft,
                },
                {
                  key: t.model.tour.sortOrderTicketBottom,
                  value: tour.sortOrderTicketBottom,
                },
              ]}
            />
          </Card>
          <TourTicketDist tour={tour} />
        </Grid>
        <Meta id={tour.id} type="category" />
        <Card fullbleed title={t.model.tour.eventsInThisTour} pagination={<Pagination {...eventsPaginator} />}>
          <EventsList
            events={events}
            error={eventsPaginator.error}
            loading={eventsPaginator.loading}
            displaySearch={false}
            emptyMessage={t.model.tour.noEvents}
          />
        </Card>
        {events.length > 0 && (
          <>
            <SalesOverTime title={t.model.sale._plural} saleId={tour.id} saleType="category" />
            <SalesByGender saleId={tour.id} saleType="category" />
            <SalesHeatmap saleId={tour.id} type="category" />
          </>
        )}
      </Stack>
      <ControlledModal<TourEdit>
        open={displayModal}
        model={{
          ...tour,
          ticketImage: tour && tour.ticketImage && tour.ticketImage.url,
        }}
        onClose={() => setDisplayModal(false)}
        title={t.actions.edit(t.model.tour._name)}
        size="small"
      >
        {(controller) => <TourForm controller={controller} />}
      </ControlledModal>
    </Container>
  );
}
